import { useMemo } from 'react'

import Button from '@/cmsComponents/ButtonV2'
import Icon from '@/components/Icon'
import Modal from '@/components/Modal'
import { useFlagGroup } from '@/contexts/flagsGroup'
import { useTonPlans } from '@/contexts/plans'
import { useReceivingOption } from '@/contexts/receivingOption'
import { sortPlansByLastFee } from '@/utils/sortPlansByLastFee'

import { TierFees } from '../TierFees'

interface PromotionalVariantProps {
  isOpen: boolean
  groupTier: string
  onClose(): void
  hiddenCTA?: boolean
}

export const PromotionalVariant = ({
  isOpen,
  onClose,
  groupTier,
  hiddenCTA = false
}: PromotionalVariantProps) => {
  const { flagsGroup } = useFlagGroup()
  const { plansByGroupTier } = useTonPlans()
  const { receivingOptionText, receivingOption } = useReceivingOption()

  const { defaultPlanTier, otherPlanTiers } = useMemo(() => {
    const planTiers = plansByGroupTier.get(groupTier) || []

    const defaultPlanTier = planTiers.find((plan) => plan.isDefaultTier)
    const otherPlanTiers = planTiers
      .filter((plan) => !plan.isDefaultTier)
      .sort(sortPlansByLastFee(receivingOption, flagsGroup))

    return { defaultPlanTier, otherPlanTiers }
  }, [flagsGroup, groupTier, plansByGroupTier, receivingOption])

  if (!defaultPlanTier) return null

  return (
    <Modal
      open={isOpen}
      closeOnClickOut={true}
      onCloseClick={onClose}
      className="flex items-end md:items-center !p-0 w-full md:max-w-[618px] h-full bg-transparent rounded-b-none"
      classNameContainer="z-[101]"
    >
      <div className="flex overflow-y-auto relative flex-col gap-12 p-24 w-full max-h-[90%] md:max-h-full bg-white md:rounded-[16px] rounded-t-24 rounded-b-none">
        <div className="flex gap-24 items-start">
          <p className="font-bold leading-5 text-display-900 ton-paragraph-20">
            Como funciona o plano {defaultPlanTier.commercial_name}?
          </p>

          <button className="ml-auto cursor-pointer" onClick={onClose}>
            <Icon name="close" className="ml-auto w-24 h-24 fill-current" />
          </button>
        </div>

        <div className="flex flex-col gap-12">
          <p className="text-center ton-paragraph-16">
            Aproveite o período promocional do {defaultPlanTier.commercial_name}{' '}
            para fazer as suas primeiras vendas.
          </p>

          <div className="flex flex-col gap-24">
            <div className="flex flex-col gap-8">
              <div className="p-8 font-bold text-center text-newTon-700 bg-newTon-50 rounded-8 ton-paragraph-14">
                Período promocional
              </div>

              <p className="text-center ton-paragraph-14">
                Taxas válidas para o recebimento da venda: {receivingOptionText}
              </p>

              <TierFees planTier={defaultPlanTier} key={defaultPlanTier.name} />
            </div>

            <div className="flex flex-col gap-16">
              <div className="p-8 font-bold text-center text-newTon-700 bg-newTon-50 rounded-8 ton-paragraph-14">
                Após o período promocional
              </div>

              <p className="text-center ton-paragraph-16">
                Você terá taxas de acordo com suas vendas mensais. No início de
                cada mês, são contabilizadas as suas vendas do mês anterior.
                Isso definirá a sua faixa de vendas e, consequentemente, as
                taxas do mês.
              </p>

              <p className="text-center ton-paragraph-14">
                Taxas válidas para o recebimento da venda: {receivingOptionText}
              </p>

              {otherPlanTiers.map((planTier) => (
                <TierFees key={planTier.name} planTier={planTier} />
              ))}

              {!hiddenCTA && (
                <>
                  <Button
                    label="Confira todas as taxas e planos"
                    linkPathname="/planos-e-taxas"
                  />

                  <button
                    className="text-newTon-700 cursor-pointer ton-paragraph-16"
                    onClick={onClose}
                  >
                    Fechar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}
